import type { MessageType } from "@expert/shared-types";
import type { DefaultMessageKey } from "../../shared-types";
import { useTimelineStore } from "../../state";
import { getQuickActions } from "./getQuickActions";
import type { MultipleActions, QuickAction } from "./types";
import { useCreateActions } from "./useCreateActions";

interface QuickActionsManagerProps {
    currentMessageType: MessageType;
    messageId?: string;
    defaultMessageKey?: DefaultMessageKey;
    isOverflowing: boolean;
    isExpanded: boolean;
    isLastBotMessage: boolean;
    isLastProactiveMessage: boolean;
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

/*
Based on design, only a single quick actions group is showing on the screen at any time.
*/
export function useQuickActionsManager({
    currentMessageType,
    messageId,
    isOverflowing,
    isExpanded,
    isLastBotMessage,
    isLastProactiveMessage,
    setIsExpanded,
}: QuickActionsManagerProps): QuickAction[] {
    const { currentSessionId, getTimelineState } = useTimelineStore();
    const { partner } = getTimelineState(currentSessionId);

    const allActions = useCreateActions({ setIsExpanded, messageId, messageType: currentMessageType });
    const currentActions: MultipleActions[] = [];

    if (isLastBotMessage || isLastProactiveMessage) {
        switch (currentMessageType) {
            case "Search": {
                if (partner === "att") {
                    currentActions.push(allActions.neutralOrPositiveFeedback, allActions.tsnr);
                } else if (partner === "mcafee") {
                    currentActions.push(allActions.additionalPromptActions);
                }
                break;
            }
            case "Proactive": {
                if (isOverflowing && !isExpanded) {
                    currentActions.push(allActions.expandMessage, allActions.negativeFeedback);
                } else {
                    currentActions.push(allActions.neutralOrPositiveFeedback, allActions.negativeFeedback);
                }
                break;
            }
            case "SessionSummary":
                if (isOverflowing && !isExpanded) {
                    currentActions.push(allActions.expandMessage);
                }
                break;
            default:
                break;
        }
    }

    const quickActions = getQuickActions(currentActions);
    return quickActions;
}
